import { BrandMark } from '@/components/shell/BrandMark';
import { Suspense, useEffect, type ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { SessionProvider, useSession, assertNever } from '@/lib/session';
import { ConnectivityProvider } from '@/lib/connectivity';
import { ToastProvider } from '@/components/ui/Toast';
import { AppShell } from '@/components/shell/AppShell';
import { PreviewBanner } from '@/components/shell/PreviewBanner';
import { LoadingState, DeniedState } from '@/components/ui/States';
import { Button, buttonClass } from '@/components/ui/Button';
import { ROUTES, type RouteDef } from '@/routes/table';
import { deniedReason } from '@/lib/capabilities';
import { supabase } from '@/lib/supabase';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { UpdateAvailable } from '@/components/shell/UpdateAvailable';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 15_000, refetchOnWindowFocus: true } },
});

function FullPage({ children, landmark = true }: { children: ReactNode; landmark?: boolean }) {
  const inner = <div className="w-full max-w-md">{children}</div>;
  const cls = 'flex min-h-dvh items-center justify-center bg-canvas px-4 py-10';
  // a loading fallback is not a landmark: React keeps a suspended tree (with its own <main>) in the DOM while it waits
  return landmark ? <main id="main" className={cls}>{inner}</main> : <div className={cls}>{inner}</div>;
}

function Notice({ title, body, action }: { title: string; body: ReactNode; action?: ReactNode }) {
  return (
    <FullPage>
      <div className="rounded-panel bg-surface p-8 elevate">
        <BrandMark className="mb-5 h-8" />
        <h1 tabIndex={-1} data-route-heading className="text-xl font-semibold text-ink outline-none">{title}</h1>
        <div className="mt-2 text-sm text-ink-2">{body}</div>
        {action && <div className="mt-6 flex flex-wrap gap-2">{action}</div>}
      </div>
    </FullPage>
  );
}

/** Gate for every authenticated route. Resolving is never read as signed out (§7). */
function RequireSession({ route, children }: { route: RouteDef; children: ReactNode }) {
  const { session, can, signOut } = useSession();
  const location = useLocation();
  const returnTo = encodeURIComponent(location.pathname + location.search);
  switch (session.state) {
    case 'loading':
      return <FullPage><LoadingState label="Checking your session…" /></FullPage>;
    case 'unauthenticated':
      return <Navigate to={`/sign-in?returnTo=${returnTo}`} replace />;
    case 'session-expired':
      return (
        <Notice
          title="Your session expired — sign in again"
          body={session.reason === 'role-changed' ? 'Your access changed since you signed in. Sign in again to continue with your current permissions.' : session.reason === 'idle' ? 'You were signed out because nothing happened for a while — your organization turned on automatic sign-out. Sign in again to return to this page.' : 'For your security you were signed out. Anything you had not saved is still on the page you came from.'}
          action={<Button variant="primary" onClick={() => void signOut().then(() => (window.location.href = `/sign-in?returnTo=${returnTo}`))}>Sign in again</Button>}
        />
      );
    case 'account-disabled':
      return <Notice title="This account is disabled" body="An administrator has turned off access for this account. Contact your organization admin if you think this is a mistake." action={<Button onClick={() => void signOut()}>Sign out</Button>} />;
    case 'no-membership':
      return <Notice title="This account isn't set up for this workspace yet" body={<>You're signed in as <strong>{session.email}</strong>, but no organization has given this account access. If your team already uses Room Map Pro, ask an admin for an invitation. Setting up for your own organization? Create it now.</>} action={<><Link to="/setup" className={buttonClass('primary')}>Set up a new organization</Link><Button onClick={() => void signOut()}>Sign out</Button></>} />;
    case 'hotel-account':
      if (route.area === 'hotel' || route.area === 'account') return <>{children}</>;
      return <Navigate to="/hotel" replace />;
    case 'authenticated-single-tenant':
      if (route.area === 'hotel') return <Navigate to="/events" replace />;
      if (route.capability && !can(route.capability)) return <AppShell><DeniedState reason={deniedReason(route.capability, session.role, route.title)} /></AppShell>;
      // pages load inside the shell: while a page's code downloads the navigation stays and only the content waits —
      // never a second full-page <main> beside the hidden shell (MISHAPS #41)
      return route.area === 'print' ? <>{children}</> : <AppShell><Suspense fallback={<LoadingState label="Loading…" />}>{children}</Suspense></AppShell>;
    default:
      return assertNever(session);
  }
}

/** "/" lands each principal where their work is; someone with exactly one live event lands on it (§10 C). */
function Home() {
  const { session } = useSession();
  const single = useQuery({
    queryKey: ['home-events'],
    enabled: session.state === 'authenticated-single-tenant',
    queryFn: async () => {
      const { data, error } = await supabase.from('events').select('id').is('deleted_at', null).neq('status', 'Archived').limit(2);
      if (error) throw error;
      return data;
    },
  });
  if (session.state === 'loading') return <FullPage><LoadingState label="Checking your session…" /></FullPage>;
  if (session.state === 'unauthenticated') return <Navigate to="/sign-in" replace />;
  if (session.state === 'hotel-account') return <Navigate to="/hotel" replace />;
  if (session.state !== 'authenticated-single-tenant') return <Navigate to="/events" replace />;
  if (single.isLoading) return <FullPage><LoadingState /></FullPage>;
  // desk staff land on the desk of their one live event; everyone else on the event's overview
  if (single.data?.length === 1) return <Navigate to={`/events/${single.data[0]!.id}${session.role === 'checkin_staff' ? '/desk' : ''}`} replace />;
  return <Navigate to="/events" replace />;
}

/** G3: an old or mistyped link says what to do next — for a guest and for someone who works on events. */
function NotFound() {
  return (
    <Notice title="We couldn't find that page" body={
      <div className="flex flex-col gap-2" data-testid="not-found">
        <p>The link may be out of date, or the page may have moved.</p>
        <p><strong className="text-ink">Registering for an event or checking your stay?</strong> Open the link in your confirmation email, or ask the organizer to send it again.</p>
        <p><strong className="text-ink">Work on events?</strong> Your workspace has everything that was here.</p>
      </div>
    } action={<Link className="font-semibold text-ink underline decoration-brand decoration-2 underline-offset-4" to="/">Go to your workspace</Link>} />
  );
}

function matchRoute(pathname: string): RouteDef | undefined {
  return ROUTES.find((x) => new RegExp(`^${x.path.replace(/:\w+/g, '[^/]+')}$`).test(pathname));
}

function TitleSync() {
  const location = useLocation();
  useEffect(() => {
    const r = matchRoute(location.pathname);
    document.title = r ? `${r.title} · Room Map Pro` : 'Room Map Pro';
  }, [location.pathname]);
  return null;
}

/** G1: the update icon where no shell carries it — public and guest pages (top right), the hotel portal (bottom right). */
function UpdateCorner() {
  const location = useLocation();
  const { session } = useSession();
  if (session.state === 'hotel-account') return <UpdateAvailable variant="corner" placement="bottom" />;
  const r = matchRoute(location.pathname);
  if (!r || r.area === 'public') return <UpdateAvailable variant="corner" placement="top" />;
  return null;
}

/** A caught error clears when the person navigates to another page. */
function RouteBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConnectivityProvider>
        <ToastProvider>
          <SessionProvider>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <TitleSync />
              <UpdateCorner />
              <PreviewBanner />
              <RouteBoundary>
                <Suspense fallback={<FullPage landmark={false}><LoadingState /></FullPage>}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    {ROUTES.map((r) => {
                      const C = r.component;
                      return (
                        <Route
                          key={r.id}
                          path={r.path}
                          element={r.area === 'public' ? <C /> : <RequireSession route={r}><RouteBoundary><C /></RouteBoundary></RequireSession>}
                        />
                      );
                    })}
                    {/* conventional addresses people type or bookmark (G3) land somewhere sensible */}
                    {[['/login', '/sign-in'], ['/signin', '/sign-in'], ['/signup', '/sign-up'], ['/register', '/sign-up'], ['/dashboard', '/'], ['/home', '/'], ['/settings', '/organization'], ['/privacy-policy', '/privacy']].map(([from, to]) => (
                      <Route key={from} path={from} element={<Navigate to={to!} replace />} />
                    ))}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </RouteBoundary>
            </BrowserRouter>
          </SessionProvider>
        </ToastProvider>
      </ConnectivityProvider>
    </QueryClientProvider>
  );
}
